
"use client";

import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

// Main Navbar Container
export const Navbar = ({ children, className, isFixed = false }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    if (isFixed) return; // Don't listen to scroll events if fixed
    
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isFixed]);

  return (
    <nav
      className={cn(
        "z-50 transition-all duration-300 shadow-lg bg-gradient-to-r from-blue-900 via-blue-700 to-blue-700",
        isFixed ? "rounded-none" : "sticky top-2 mx-2 mt-1 rounded-2xl",
        className
      )}
    >
      {children}
    </nav>
  );
};

// Desktop Navigation Body
export const NavBody = ({ children, className }) => {
  return (
    <div
      className={cn(
        "hidden lg:flex items-center justify-between px-2 py-4 w-full",
        className
      )}
    >
      {children}
    </div>
  );
};

// Navbar Logo Component
export const NavbarLogo = ({ logo, alt = "Logo", className }) => {
  return (
    <div className={cn("flex items-center", className)}>
      {logo ? (
        <img src={logo} alt={alt} className="h-10 w-auto object-contain" />
      ) : (
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#0263cc] to-[#0150a8] flex items-center justify-center">
            <span className="text-white font-bold text-xl">C</span>
          </div>
          <span className="text-xl font-bold text-gray-900">Clynox</span>
        </div>
      )}
    </div>
  );
};

// Navigation Items
export const NavItems = ({ items, className }) => {
  return (
    <div className={cn("flex items-center gap-1", className)}>
      {items.map((item, idx) => (
        <a
          key={`nav-item-${idx}`}
          href={item.link}
          className={cn(
            "px-2.5 py-1.5 rounded-md text-sm font-medium transition-all duration-200",
            "text-white hover:bg-white/10 hover:text-gray-100",
            "relative group"
          )}
        >
          {item.name}
        </a>
      ))}
    </div>
  );
};

// Navbar Buttons
export const NavbarButton = ({
  children,
  variant = "primary",
  className,
  onClick,
  ...props
}) => {
  const variants = {
    primary: "bg-[#0263cc] text-white hover:bg-[#0150a8] shadow-md hover:shadow-lg",
    secondary:
      "border-2 border-[#0263cc] text-[#0263cc] hover:bg-[#0263cc] hover:text-white",
  };

  return (
    <button
      onClick={onClick}
      className={cn(
        "px-6 py-2.5 rounded-lg font-medium transition-all duration-200",
        "transform hover:scale-105 active:scale-95",
        variants[variant],
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
};

// Mobile Navigation Container
export const MobileNav = ({ children, className }) => {
  return (
    <div className={cn("lg:hidden", className)}>
      {children}
    </div>
  );
};

// Mobile Navigation Header
export const MobileNavHeader = ({ children, className }) => {
  return (
    <div
      className={cn(
        "flex items-center justify-between px-6 py-4 relative",
        className
      )}
    >
      {children}
    </div>
  );
};

// Mobile Menu Toggle Button
export const MobileNavToggle = ({ isOpen, onClick, className }) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        "p-2 rounded-lg transition-colors duration-200",
        "text-white hover:bg-[#0255b3]",
        "focus:outline-none focus:ring-2 focus:ring-white/20",
        className
      )}
      aria-label="Toggle menu"
    >
      {isOpen ? (
        <X className="h-6 w-6" />
      ) : (
        <Menu className="h-6 w-6" />
      )}
    </button>
  );
};

// Mobile Menu with Slide Animation
export const MobileNavMenu = ({ isOpen, onClose, children, className }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm lg:hidden z-40"
            onClick={onClose}
          />

          {/* Sliding Menu */}
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30
            }}
            className={cn(
              "fixed inset-y-0 right-0 w-80 max-w-[90vw] bg-white shadow-2xl lg:hidden overflow-y-auto z-50",
              className
            )}
          >
            <motion.div
              className="flex flex-col gap-3 p-5"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.3 }}
            >
              {children}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// Collapsible Section Component (for nested menus)
export const CollapsibleSection = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-gray-200 pb-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full text-left font-medium text-gray-900 hover:text-[#0263cc] transition-colors duration-200"
      >
        <span>{title}</span>
        <motion.svg
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </motion.svg>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{
              duration: 0.3,
              ease: "easeInOut"
            }}
            className="overflow-hidden"
          >
            <motion.div
              className="space-y-2 mt-3"
              initial={{ y: -10 }}
              animate={{ y: 0 }}
              transition={{ delay: 0.1 }}
            >
              {children}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
